import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MousePointer2 } from "lucide-react";

// Self-contained copy of the real Navigator's spotlight visual
// (src/components/portal/NavigatorSpotlight.jsx), deliberately forked rather
// than imported: /portal-preview is public/unauthenticated and must never
// share code paths, target catalogs, or state with the real, authenticated
// Navigator system. Same highlight-ring + pointer mechanic, driven here by a
// fixed tour script (DemoNavigatorTour.jsx) instead of live agent output.

const POLL_MS = 200;
const MAX_POLLS = 20;

export default function DemoNavigatorSpotlight({ targetId }) {
  const [rect, setRect] = useState(null);
  const pollRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    setRect(null);
    clearTimeout(pollRef.current);
    cancelAnimationFrame(rafRef.current);
    if (!targetId) return;

    let attempts = 0;
    const tryLocate = () => {
      const el = document.querySelector(`[data-navigator-id="${targetId}"]`);
      if (el) {
        const track = () => {
          const r = el.getBoundingClientRect();
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
          rafRef.current = requestAnimationFrame(track);
        };
        track();
        return;
      }
      attempts += 1;
      if (attempts < MAX_POLLS) pollRef.current = setTimeout(tryLocate, POLL_MS);
    };
    tryLocate();

    return () => {
      clearTimeout(pollRef.current);
      cancelAnimationFrame(rafRef.current);
    };
  }, [targetId]);

  return (
    <AnimatePresence>
      {rect && (
        <motion.div
          key="demo-spotlight-ring"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed pointer-events-none z-40"
          style={{ top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12 }}
        >
          <motion.div
            className="w-full h-full rounded-xl border-2 border-secondary"
            animate={{ boxShadow: ["0 0 0 0 rgba(217,119,6,0.45)", "0 0 0 8px rgba(217,119,6,0)"] }}
            transition={{ duration: 1.3, repeat: Infinity, ease: "easeOut" }}
          />
          <motion.div
            className="absolute -bottom-3 -right-3 bg-secondary text-secondary-foreground rounded-full p-1.5 shadow-lg"
            initial={{ x: 14, y: -14, opacity: 0 }}
            animate={{ x: 0, y: 0, opacity: 1 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
          >
            <motion.div animate={{ x: [0, -3, 0], y: [0, -3, 0] }} transition={{ duration: 1, repeat: Infinity }}>
              <MousePointer2 className="w-4 h-4" fill="currentColor" />
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
